import { Injectable } from '@angular/core';
import { DeduplicationFramework } from './deduplication-framework.service';

/**
 * Telemetry Filter - Intelligent event filtering based on Information Theory principles.
 * Filters out zero-information events and deduplicates rapid-fire events.
 * Based on the evidence that 65% of WebSocket chunk events have zero duration.
 */
@Injectable({ providedIn: 'root' })
export class TelemetryFilter {
  private eventStats = new Map<string, { total: number; filtered: number }>();
  private readonly MIN_INTERVAL_MS = 10; // Minimum interval between duplicate events

  constructor(private dedup: DeduplicationFramework) {}

  /**
   * Track an event with intelligent filtering
   * @param eventName Name of the event
   * @param data Event data
   * @returns true if event was tracked, false if filtered
   */
  trackEvent(eventName: string, data: any): boolean {
    // Update statistics
    this.updateStats(eventName, false);

    // Filter 1: Zero-information events (Information Theory principle)
    if (this.isZeroInformationEvent(eventName, data)) {
      this.updateStats(eventName, true);
      console.debug(`[TelemetryFilter] Filtered zero-information event: ${eventName}`);
      return false;
    }

    // Filter 2: Empty or null data
    if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
      this.updateStats(eventName, true);
      console.debug(`[TelemetryFilter] Filtered empty event: ${eventName}`);
      return false;
    }

    // Filter 3: Deduplicate rapid-fire events
    const eventKey = this.generateEventKey(eventName, data);
    if (!this.dedup.deduplicateEvent(eventKey, data, this.MIN_INTERVAL_MS)) {
      this.updateStats(eventName, true);
      console.debug(`[TelemetryFilter] Filtered duplicate event within ${this.MIN_INTERVAL_MS}ms: ${eventName}`);
      return false;
    }

    // Event passed all filters - allow tracking
    return true;
  }

  /**
   * Check if an event contains zero information (should be filtered)
   * @param eventName Name of the event
   * @param data Event data
   * @returns true if event should be filtered
   */
  private isZeroInformationEvent(eventName: string, data: any): boolean {
    // WebSocket chunk events with no content
    if (eventName === 'chat_message_chunk_received') {
      if (!data.content || data.content.length === 0) {
        return true;
      }
      if (data.duration === 0 && !data.error) {
        return true;
      }
    }

    // Connection status events that don't actually change status
    if (eventName === 'connection_status_changed') {
      if (!data.previousStatus || data.previousStatus === data.currentStatus) {
        return true;
      }
    }

    // Events with zero duration and no meaningful data
    if (data.duration === 0 && !data.error && !data.result) {
      return true;
    }

    // Events that are just heartbeats or keep-alives
    if (eventName.includes('heartbeat') || eventName.includes('ping') || eventName.includes('keepalive')) {
      return true;
    }

    return false;
  }

  /**
   * Generate a unique key for an event to detect duplicates
   * @param eventName Name of the event
   * @param data Event data
   * @returns Unique key for the event
   */
  private generateEventKey(eventName: string, data: any): string {
    // For simple data types, use the value directly
    if (typeof data !== 'object') {
      return `${eventName}:${data}`;
    }

    // For objects, create a key based on significant properties
    const significantProps = this.extractSignificantProperties(data);
    return `${eventName}:${JSON.stringify(significantProps)}`;
  }

  /**
   * Extract only significant properties from event data
   * @param data Event data
   * @returns Object with only significant properties
   */
  private extractSignificantProperties(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }

    // Remove timestamp-like properties that change every time
    const filtered: any = {};
    const ignoredKeys = ['timestamp', 'time', 'createdAt', 'updatedAt', '_id', 'id'];

    for (const key in data) {
      if (!ignoredKeys.includes(key) && data[key] !== undefined) {
        filtered[key] = data[key];
      }
    }

    return filtered;
  }

  /**
   * Update event statistics
   * @param eventName Name of the event
   * @param wasFiltered Whether the event was filtered
   */
  private updateStats(eventName: string, wasFiltered: boolean): void {
    const stats = this.eventStats.get(eventName) || { total: 0, filtered: 0 };
    stats.total++;
    if (wasFiltered) {
      stats.filtered++;
    }
    this.eventStats.set(eventName, stats);
  }

  /**
   * Get telemetry filtering statistics
   */
  getFilterStats(): {
    eventStats: Array<{
      eventName: string;
      total: number;
      filtered: number;
      filterRate: number;
    }>;
    totalEvents: number;
    totalFiltered: number;
    overallFilterRate: number;
  } {
    let totalEvents = 0;
    let totalFiltered = 0;

    const eventStats = Array.from(this.eventStats.entries()).map(([eventName, stats]) => {
      totalEvents += stats.total;
      totalFiltered += stats.filtered;

      return {
        eventName,
        total: stats.total,
        filtered: stats.filtered,
        filterRate: stats.total > 0 ? (stats.filtered / stats.total) * 100 : 0
      };
    });

    const overallFilterRate = totalEvents > 0 ? (totalFiltered / totalEvents) * 100 : 0;

    return {
      eventStats,
      totalEvents,
      totalFiltered,
      overallFilterRate
    };
  }

  /**
   * Reset statistics (useful for testing)
   */
  resetStats(): void {
    this.eventStats.clear();
  }

  /**
   * Get recommended filter settings based on current statistics
   */
  getRecommendations(): string[] {
    const recommendations: string[] = [];
    const stats = this.getFilterStats();

    // Check for events with high filter rates
    for (const eventStat of stats.eventStats) {
      if (eventStat.filterRate > 80) {
        recommendations.push(
          `Consider removing ${eventStat.eventName} tracking - ${eventStat.filterRate.toFixed(1)}% filtered`
        );
      }
    }

    // Check overall filter rate
    if (stats.overallFilterRate > 60) {
      recommendations.push(
        `High overall filter rate (${stats.overallFilterRate.toFixed(1)}%) - review telemetry strategy`
      );
    }

    // Check for specific problem patterns
    const chunkEvents = this.eventStats.get('chat_message_chunk_received');
    if (chunkEvents && chunkEvents.filtered > 50) {
      recommendations.push(
        'Many empty WebSocket chunks detected - consider batching or compression'
      );
    }

    return recommendations;
  }
}